import { useState } from 'react';
import { Dropdown, Button } from 'antd';
import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { exportToExcel, exportToPdf } from '../utils/exportUtils';
import logoBncb from '../assets/images/logo.png';
import BoutonFlottant, { STYLE_BOUTON_FLOTTANT } from './BoutonFlottant';

/**
 * Bouton d'export universel - Excel + PDF.
 *
 * Props :
 *   - data            : tableau d'objets (lignes à exporter) - utilisé si dataLoader absent
 *   - dataLoader      : async () => rows - appelé au clic, supplante data
 *   - columns         : colonnes AntD standard (title + dataIndex + format? + render?)
 *                       Ajouter `exportable: false` sur les colonnes d'action à exclure.
 *                       Ajouter `format: 'currency'|'date'|'datetime'|'bool'` pour formattage propre.
 *                       Ajouter `numeric: true` sur colonnes numériques (totaux).
 *                       Ajouter `export: (v) => string` pour surcharger la valeur exportée.
 *   - filename        : base du nom de fichier (sans extension)
 *   - title / subtitle: en-tête du document
 *   - size            : taille du bouton AntD (défaut middle)
 *   - disabled        : désactivé si data vide
 *   - floating        : position fixée bas-droite (défaut false)
 *   - backgroundColor : couleur de fond du bouton en mode floating (défaut #1a6b3c)
 *   - color           : couleur du texte/icône en mode floating (défaut #ffffff)
 *   - count           : nombre affiché entre () sur le bouton (défaut null)
 *   - loadingLabel    : label pendant un dataLoader async (défaut 'Préparation…')
 *   - logo            : URL webpack (import) ou dataURL d'un logo à embarquer en haut-gauche
 *                       du PDF. DEFAUT : le logo du Bureau.
 *
 *                       POURQUOI UN DEFAUT ET NON `null`. La prop etait facultative,
 *                       et vingt des quarante-cinq ecrans qui exportent l'avaient
 *                       oubliee : leurs PDF sortaient sans logo, a cote de ceux qui
 *                       en portaient un. Rien ne le signalait - un logo absent ne
 *                       leve pas. Un document officiel du Bureau porte son logo ;
 *                       c'est donc l'absence qui doit se declarer, en passant
 *                       explicitement `logo={null}`.
 *   - didParseCell    : callback jsPDF-autotable pour coloration/style personnalisé des cellules PDF (défaut null)
 */
export default function TableExportButton({
  data, dataLoader, columns, filename, title, subtitle,
  size = 'middle', disabled,
  floating = false,
  backgroundColor = '#1a6b3c',
  color = '#ffffff',
  count = null,
  loadingLabel,
  logo = logoBncb,
  didParseCell = null,  // NEW: pass-through for custom row coloring in PDF
  showTotals = true,    // NEW: désactive la ligne TOTAL si l'export contient déjà des totaux
  /* ORIENTATION ET PLACE DU LOGO, transmises a `exportToPdf`.

     Elles manquaient, et leur absence etait un piege : `exportToPdf` les accepte depuis
     toujours, mais ce composant ne les passait pas. Un ecran qui en avait besoin devait
     donc appeler `exportToPdf` directement - et posait ses deux boutons a la main, au
     lieu du menu unique que ce composant offre.

     `orientation` laissee vide, `exportToPdf` decide seul : paysage au-dela de six
     colonnes. C'est insuffisant pour un tableau de SIX colonnes dont deux longues, que le
     portrait ecrase - d'ou le besoin de pouvoir le dire. */
  orientation,
  logoPosition,
}) {
  const { t } = useTranslation('commun');
  const [loading, setLoading] = useState(false);
  const isDisabled = disabled || (!dataLoader && (!data || data.length === 0));


  const runExport = async (kind) => {
    try {
      setLoading(true);
      let rows = data;
      if (typeof dataLoader === 'function') {
        rows = await dataLoader();
      }
      if (!rows || rows.length === 0) return;
      const args = {
        data: rows, columns, filename, title, subtitle, logo, didParseCell, showTotals,
        orientation, logoPosition,
      };
      if (kind === 'excel') exportToExcel(args);
      else await exportToPdf(args);
    } finally {
      setLoading(false);
    }
  };

  const items = [
    {
      key: 'excel',
      label: t('tableExportButton.exporterExcel'),
      icon: <FileExcelOutlined style={{ color: '#107c41' }} />,
      onClick: () => runExport('excel'),
    },
    {
      key: 'pdf',
      label: t('tableExportButton.exporterPdf'),
      icon: <FilePdfOutlined style={{ color: '#dc2626' }} />,
      onClick: () => runExport('pdf'),
    },
  ];

  const buttonStyle = floating ? {
    background: backgroundColor,
    borderColor: backgroundColor,
    color,
    ...STYLE_BOUTON_FLOTTANT,
  } : undefined;

  const buttonNode = (
    <Button
      icon={<DownloadOutlined />}
      size={floating ? 'large' : size}
      type={floating ? 'primary' : 'default'}
      disabled={isDisabled}
      loading={loading}
      style={buttonStyle}
    >
      {loading ? (loadingLabel === undefined ? t('tableExportButton.preparation') : loadingLabel)
        : (count != null ? t('tableExportButton.exporterAvecNombre', { nombre: count }) : t('tableExportButton.exporter'))}
    </Button>
  );

  const dropdown = (
    <Dropdown
      menu={{ items }}
      disabled={isDisabled}
      trigger={['click']}
      placement={floating ? 'topRight' : 'bottomRight'}
    >
      {buttonNode}
    </Dropdown>
  );

  /* Le positionnement et la reserve de place vivent dans `BoutonFlottant` : ils
     servent aussi a l'export serveur des etats partenaires, qui ne passe pas par
     ce composant. */
  if (floating) return <BoutonFlottant>{dropdown}</BoutonFlottant>;
  return dropdown;
}
